function _slicedToArray(r, e) {
  return _arrayWithHoles(r) || _iterableToArrayLimit(r, e) || _unsupportedIterableToArray(r, e) || _nonIterableRest();
}
import PropTypes from "prop-types";
function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _unsupportedIterableToArray(r, a) {
  if (r) {
    if ("string" == typeof r) return _arrayLikeToArray(r, a);
    var t = {}.toString.call(r).slice(8, -1);
    return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0;
  }
}
function _arrayLikeToArray(r, a) {
  (null == a || a > r.length) && (a = r.length);
  for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e];
  return n;
}
function _iterableToArrayLimit(r, l) {
  var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"];
  if (null != t) {
    var e,
      n,
      i,
      u,
      a = [],
      f = !0,
      o = !1;
    try {
      if (i = (t = t.call(r)).next, 0 === l) {
        if (Object(t) !== t) return;
        f = !1;
      } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0);
    } catch (r) {
      o = !0, n = r;
    } finally {
      try {
        if (!f && null != t.return && (u = t.return(), Object(u) !== u)) return;
      } finally {
        if (o) throw n;
      }
    }
    return a;
  }
}
function _arrayWithHoles(r) {
  if (Array.isArray(r)) return r;
}
/*
 * SPDX-License-Identifier: Apache-2.0
 *
 * The OpenSearch Contributors require contributions made to
 * this file be licensed under the Apache-2.0 license or a
 * compatible open source license.
 *
 * Modifications Copyright OpenSearch Contributors. See
 * GitHub history for details.
 */

/*
 * Licensed to Elasticsearch B.V. under one or more contributor
 * license agreements. See the NOTICE file distributed with
 * this work for additional information regarding copyright
 * ownership. Elasticsearch B.V. licenses this file to you under
 * the Apache License, Version 2.0 (the "License"); you may
 * not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import React, { Fragment, useEffect, useState } from 'react';
import classNames from 'classnames';
import { OuiI18n } from '../../i18n';
import { OuiInnerText } from '../../inner_text';
import { OuiLink } from '../../link';
import { OuiPopover } from '../../popover';
import { OuiIcon } from '../../icon';
import { throttle } from '../../../services';
import { getBreakpoint } from '../../../services/breakpoint';
/* Try to fit breadcrumbs with at least 135px into the width of each of the BREAKPOINTS
 *    1. put aside 250px for the last breadcrumb and possible loss due to layout
 *    2. provide at least 135px for each remaining breadcrumb
 *
 *     * OuiBreadcrumbs has 48px padding on the side with a max-width of 160px
 *     * OuiSimplifiedBreadcrumbs has 24px for gap and separator
 *     * To be uniform
 *           160px - 48px + 24px ≈ 135px for each simplified breadcrumb
 *           275px - 48px + 24px ≈ 250px for last simplified breadcrumb and external spacing
 *
 *    numberOfBreadcrumbs = (breakpointWidth - 251) / 136 + 1
 */
var responsiveDefault = {
  xs: 1,
  // Show only one
  s: 3,
  //  (575 - 250) / 135 + 1 = 3.40
  m: 4,
  //  (768 - 250) / 135 + 1 = 4.83
  l: 6,
  //  (992 - 250) / 135 + 1 = 6.49
  xl: 8,
  // (1200 - 250) / 135 + 1 = 8.04
  xxl: 11,
  // (1680 - 250) / 135 + 1 = 11.59
  xxxl: 13 // (1920 - 250) / 135 + 1 = 13.37
};
/*
  xxxl: 1920,
  xxl: 1680,
  xl: 1200,
  l: 992,
  m: 768,
  s: 575,
 */

var limitBreadcrumbs = function limitBreadcrumbs(breadcrumbs, max, allBreadcrumbs) {
  var breadcrumbsAtStart = [];
  var breadcrumbsAtEnd = [];
  var limit = Math.min(max, breadcrumbs.length);
  var start = Math.floor(limit / 2);
  var overflowBreadcrumbs = allBreadcrumbs.slice(start, start + breadcrumbs.length - limit);
  for (var i = 0; i < limit; i++) {
    // We'll alternate with displaying breadcrumbs at the end and at the start, but be biased
    // towards breadcrumbs the end so that if max is an odd number, we'll have one more
    // breadcrumb visible at the end than at the beginning.
    var isEven = i % 2 === 0;

    // We're picking breadcrumbs from the front AND the back, so we treat each iteration as a
    // half-iteration.
    var normalizedIndex = Math.floor(i * 0.5);
    var indexOfBreadcrumb = isEven ? breadcrumbs.length - 1 - normalizedIndex : normalizedIndex;
    var breadcrumb = breadcrumbs[indexOfBreadcrumb];
    if (isEven) {
      breadcrumbsAtEnd.unshift(breadcrumb);
    } else {
      breadcrumbsAtStart.push(breadcrumb);
    }
  }
  var OuiBreadcrumbCollapsed = function OuiBreadcrumbCollapsed() {
    var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isPopoverOpen = _useState2[0],
      setIsPopoverOpen = _useState2[1];
    var ellipsisButton = /*#__PURE__*/React.createElement(OuiI18n, {
      token: "ouiSimplifiedBreadcrumbs.collapsedBadge.ariaLabel",
      default: "Show collapsed breadcrumbs"
    }, function (ariaLabel) {
      return /*#__PURE__*/React.createElement(OuiLink, {
        className: "ouiSimplifiedBreadcrumb ouiSimplifiedBreadcrumb__collapsedLink",
        "aria-label": ariaLabel,
        title: ariaLabel,
        onClick: function onClick() {
          return setIsPopoverOpen(!isPopoverOpen);
        }
      }, "\u2026 ", /*#__PURE__*/React.createElement(OuiIcon, {
        type: "arrowDown",
        size: "s"
      }));
    });
    return /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement(OuiPopover, {
      className: "ouiSimplifiedBreadcrumb ouiSimplifiedBreadcrumb--collapsed",
      button: ellipsisButton,
      isOpen: isPopoverOpen,
      closePopover: function closePopover() {
        return setIsPopoverOpen(false);
      }
    }, /*#__PURE__*/React.createElement(OuiSimplifiedBreadcrumbs, {
      className: "ouiSimplifiedBreadcrumbs__inPopover",
      breadcrumbs: overflowBreadcrumbs,
      responsive: false,
      truncate: false,
      hideLastBreadCrumb: false,
      hideTrailingSeparator: true,
      disableTrailingLink: false,
      max: 0
    })), /*#__PURE__*/React.createElement(OuiBreadcrumbSeparator, null));
  };
  if (max < breadcrumbs.length) {
    breadcrumbsAtStart.push(/*#__PURE__*/React.createElement(OuiBreadcrumbCollapsed, {
      key: "collapsed"
    }));
  }
  return [].concat(breadcrumbsAtStart, breadcrumbsAtEnd);
};
var OuiBreadcrumbSeparator = function OuiBreadcrumbSeparator() {
  return (/*#__PURE__*/
    // preserveAspectRatio is none so we can stretch it vertically and keep the width fixed
    React.createElement("svg", {
      xmlns: "http://www.w3.org/2000/svg",
      viewBox: "0 0 8 12",
      preserveAspectRatio: "none",
      className: "ouiBreadcrumbSeparator"
    }, /*#__PURE__*/React.createElement("path", {
      fill: "currentColor",
      d: "M2 12H0L6 0h2z"
    }))
  );
};
export var OuiSimplifiedBreadcrumbs = ({
  breadcrumbs,
  className,
  responsive = responsiveDefault,
  truncate = true,
  max = 5,
  hideTrailingSeparator,
  disableTrailingLink,
  hideLastBreadCrumb,
  ...rest
}) => {
  // Use the default object if they simply passed `true` for responsive
  const responsiveObject = typeof responsive === 'object' ? responsive : responsiveDefault;
  const allowedBreakpoints = responsive ? Object.keys(responsiveObject) : undefined;
  const [currentBreakpoint, setCurrentBreakpoint] = useState(getBreakpoint(typeof window === 'undefined' ? -Infinity : window.innerWidth, undefined, allowedBreakpoints));
  const functionToCallOnWindowResize = throttle(() => {
    const newBreakpoint = getBreakpoint(window.innerWidth, undefined, allowedBreakpoints);
    if (newBreakpoint !== currentBreakpoint) {
      setCurrentBreakpoint(newBreakpoint);
    }
    // reacts every 50ms to resize changes and always gets the final update
  }, 50);

  // Add window resize handlers
  useEffect(() => {
    window.addEventListener('resize', functionToCallOnWindowResize);
    return () => {
      window.removeEventListener('resize', functionToCallOnWindowResize);
    };
  }, [responsive, responsiveObject, functionToCallOnWindowResize]);
  const isInPopover = className === 'ouiSimplifiedBreadcrumbs__inPopover';
  const breadcrumbElements = breadcrumbs.map((breadcrumb, index) => {
    const {
      text,
      href,
      onClick,
      truncate,
      className: breadcrumbClassName,
      ...breadcrumbRest
    } = breadcrumb;
    const breadcrumbsLength = breadcrumbs.length;
    const isLastBreadcrumb = index === breadcrumbsLength - 1;
    if (isLastBreadcrumb && hideLastBreadCrumb) return null;
    const isFirstBreadcrumb = index === 0;
    // If hideLastBreadCrumb, the trailing breadcrumb would be the one before the last
    const isTrailingBreadcrumb = index >= breadcrumbsLength - (hideLastBreadCrumb ? 2 : 1);
    const breadcrumbClasses = classNames('ouiSimplifiedBreadcrumb', breadcrumbClassName, {
      'ouiSimplifiedBreadcrumb--last': isLastBreadcrumb,
      'ouiSimplifiedBreadcrumb--truncate': truncate
    });
    const link = !(href || onClick) || isTrailingBreadcrumb && disableTrailingLink ? <OuiInnerText>
          {(ref, innerText) => <span ref={ref} className={breadcrumbClasses} title={innerText} aria-current={isLastBreadcrumb && !isInPopover ? 'page' : 'false'} {...breadcrumbRest}>
              {text}
            </span>}
        </OuiInnerText> : <OuiInnerText>
          {(ref, innerText) => <OuiLink ref={ref} onClick={onClick} href={href} className={breadcrumbClasses} title={innerText} {...breadcrumbRest}>
              {text}
            </OuiLink>}
        </OuiInnerText>;
    const wall = isFirstBreadcrumb ? <div className={classNames('ouiSimplifiedBreadcrumbWall', {
      'ouiSimplifiedBreadcrumbWall--single': isFirstBreadcrumb && isLastBreadcrumb
    })}>
        {link}
      </div> : link;
    const separator = hideTrailingSeparator && isTrailingBreadcrumb ? null : <OuiBreadcrumbSeparator />;
    return <Fragment key={index}>
        {wall}
        {separator}
      </Fragment>;
  });

  // The max property collapses any breadcrumbs past the max quantity.
  // This is the same behavior we want for responsiveness.
  // So calculate the max value based on the combination of `max` and `responsive`

  // First, calculate the responsive max value
  const responsiveMax = responsive && responsiveObject[currentBreakpoint] || null;

  // Second, if both max and responsiveMax are set, use the smaller of the two. Otherwise, use the one that is set.
  const calculatedMax = max && responsiveMax ? Math.min(max, responsiveMax) : max || responsiveMax;
  const limitedBreadcrumbs = calculatedMax ? limitBreadcrumbs(breadcrumbElements, calculatedMax, breadcrumbs) : breadcrumbElements;
  const classes = classNames('ouiSimplifiedBreadcrumbs', className, {
    'ouiSimplifiedBreadcrumbs--truncate': truncate
  });
  return <nav aria-label="breadcrumb" className={classes} {...rest}>
      {limitedBreadcrumbs}
    </nav>;
};
OuiSimplifiedBreadcrumbs.propTypes = {
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,
  /**
     * Hides extra (above the max) breadcrumbs under a collapsed item as the window gets smaller.
     * Pass a custom #OuiBreadcrumbResponsiveMaxCount object to change the number of breadcrumbs to show at the particular breakpoints.
     * Omitting or passing a `0` value will show all breadcrumbs.
     *
     * Pass `false` to turn this behavior off.
     *
     * Default: `{ xs: 1, s: 2, m: 4 }`
     */
  responsive: PropTypes.oneOfType([PropTypes.bool.isRequired, PropTypes.any.isRequired]),
  /**
     * Forces all breadcrumbs to single line and
     * truncates each breadcrumb to a particular width,
     * except for the last item
     */
  truncate: PropTypes.bool,
  /**
     * Collapses the inner items past the maximum set here
     * into a single ellipses item
     */
  max: PropTypes.oneOfType([PropTypes.number.isRequired, PropTypes.oneOf([null])]),
  /**
     * The array of individual #OuiBreadcrumb items
     */
  breadcrumbs: PropTypes.arrayOf(PropTypes.shape({
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string,
    /**
       * Visible label of the breadcrumb
       */
    text: PropTypes.node.isRequired,
    href: PropTypes.string,
    onClick: PropTypes.func,
    /**
       * Force a max-width on the breadcrumb text
       */
    truncate: PropTypes.bool
  }).isRequired).isRequired,
  hideTrailingSeparator: PropTypes.bool,
  disableTrailingLink: PropTypes.bool,
  hideLastBreadCrumb: PropTypes.bool
};
try {
  OuiSimplifiedBreadcrumbs.__docgenInfo = {
    tags: {},
    filePath: '/codebuild/output/src3672379046/src/src/components/breadcrumbs/simplified_breadcrumbs/simplified_breadcrumbs.tsx',
    description: '',
    displayName: 'OuiSimplifiedBreadcrumbs',
    methods: [],
    props: {
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: '',
        name: 'aria-label',
        parent: {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      responsive: {
        defaultValue: {
          value: '{\n' + '  xs: 1, // Show only one\n' + '  s: 3, //  (575 - 250) / 135 + 1 = 3.40\n' + '  m: 4, //  (768 - 250) / 135 + 1 = 4.83\n' + '  l: 6, //  (992 - 250) / 135 + 1 = 6.49\n' + '  xl: 8, // (1200 - 250) / 135 + 1 = 8.04\n' + '  xxl: 11, // (1680 - 250) / 135 + 1 = 11.59\n' + '  xxxl: 13, // (1920 - 250) / 135 + 1 = 13.37\n' + '}'
        },
        description: 'Hides extra (above the max) breadcrumbs under a collapsed item as the window gets smaller.\n' + 'Pass a custom #OuiBreadcrumbResponsiveMaxCount object to change the number of breadcrumbs to show at the particular breakpoints.\n' + 'Omitting or passing a `0` value will show all breadcrumbs.\n' + '\n' + 'Pass `false` to turn this behavior off.\n' + '\n' + 'Default: `{ xs: 1, s: 2, m: 4 }`',
        name: 'responsive',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/breadcrumbs/breadcrumbs.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean | OuiBreadcrumbResponsiveMaxCount'
        }
      },
      truncate: {
        defaultValue: {
          value: 'true'
        },
        description: 'Forces all breadcrumbs to single line and\n' + 'truncates each breadcrumb to a particular width,\n' + 'except for the last item',
        name: 'truncate',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/breadcrumbs/breadcrumbs.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      max: {
        defaultValue: {
          value: '5'
        },
        description: 'Collapses the inner items past the maximum set here\n' + 'into a single ellipses item',
        name: 'max',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/breadcrumbs/breadcrumbs.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'number'
        }
      },
      breadcrumbs: {
        defaultValue: null,
        description: 'The array of individual #OuiBreadcrumb items',
        name: 'breadcrumbs',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/breadcrumbs/breadcrumbs.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: 'OuiBreadcrumb[]'
        }
      },
      hideTrailingSeparator: {
        defaultValue: null,
        description: '',
        name: 'hideTrailingSeparator',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/breadcrumbs/simplified_breadcrumbs/simplified_breadcrumbs.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      disableTrailingLink: {
        defaultValue: null,
        description: '',
        name: 'disableTrailingLink',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/breadcrumbs/simplified_breadcrumbs/simplified_breadcrumbs.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      hideLastBreadCrumb: {
        defaultValue: null,
        description: '',
        name: 'hideLastBreadCrumb',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/breadcrumbs/simplified_breadcrumbs/simplified_breadcrumbs.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      }
    },
    extendedInterfaces: ['CommonProps']
  };
} catch (e) {}